
import { Idea, Section, Attachment } from '../types';

interface ParsedIdea {
    title: string;
    content: string;
    sections: Section[];
    attachments: Attachment[];
}

const generateId = () => Math.random().toString(36).substring(2, 15);

// Regex for extracting links [Name](URL)
const LINK_REGEX = /\[([^\]]+)\]\(([^)]+)\)/g;

// Helper to extract attachments from text
const extractAttachments = (text: string, sectionId?: string): { cleanText: string, attachments: Attachment[] } => {
    const attachments: Attachment[] = [];
    const cleanText = text.replace(LINK_REGEX, (match, name, url) => {
        // Simple heuristic: if it looks like a file, treat as attachment
        // Otherwise keep as link (though for now we can treat all as potential attachments or just links)
        // For the requested feature, we'll treat them as "File Links"

        // Check if URL is a file or external link? 
        // For now, let's treat ALL links in this format as "Attachments" if they are on their own line?
        // Or just collect them.

        attachments.push({
            id: generateId(),
            name: name,
            type: 'document', // Default to document/link
            mimeType: 'application/link', // Special type for links
            driveWebLink: url,
            timestamp: Date.now(),
            sectionId: sectionId
        });

        // Return empty string to remove from content, OR keep it as a link?
        // User requested "manual add to the document copy would be fit for importing... attachment add feature"
        // Let's keep it in the text as a hyperlink for context, but ALSO add to attachments list.
        return match;
    });

    return { cleanText, attachments };
};

export const parseImportText = (text: string, defaultTitle: string = "Imported Project"): ParsedIdea => {
    const lines = text.split(/\r?\n/);

    let title = defaultTitle;
    let projectContentLines: string[] = [];

    const sections: Section[] = [];
    const attachments: Attachment[] = [];

    let currentSection: Section | null = null;
    let currentSectionContent: string[] = [];

    // Stack to track hierarchy: [Depth 0 Section, Depth 1 Section, ...]
    const sectionStack: (Section | null)[] = [null, null, null];

    // Helper to finalize current section before starting new one
    const finalizeCurrentSection = () => {
        if (currentSection) {
            const rawContent = currentSectionContent.join('\n').trim();
            const { cleanText, attachments: newAttachments } = extractAttachments(rawContent, currentSection.id);

            currentSection.content = cleanText; // In future, could run Markdown -> HTML converter here
            attachments.push(...newAttachments);

            sections.push(currentSection);
        }
    };

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmed = line.trim();

        // Project Title (# Title)
        if (trimmed.startsWith('# ')) {
            // If we already have a title (and it's not the default), maybe this is just a big header?
            // But strict format says # is Title.
            // If we are mid-parsing, we might treat this as a restart or just update title?
            // Let's assume the FIRST # is the title. Subsequent # might be H1 inside content? 
            // The request implies "Import Project", so # is likely the project Root.

            if (currentSection) finalizeCurrentSection();
            currentSection = null;
            currentSectionContent = [];

            title = trimmed.substring(2).trim();
            continue;
        }

        // Sections (## Category, ### Sub, #### Sub-Sub)
        if (trimmed.startsWith('##')) {
            finalizeCurrentSection();

            let depth = 0; // Default ## (Category)
            let headerTitle = "";
            let parentId: string | null = null;

            if (trimmed.startsWith('#### ')) {
                depth = 2; // Sub-subcategory
                headerTitle = trimmed.substring(5).trim();
                // Parent is the last seen Depth 1
                parentId = sectionStack[1]?.id || null;
                // If no parent at depth 1, we might need to orphan it or attach to root? 
                // Let's attach to whatever is at depth 1. If null, maybe attach to depth 0?
                // Logic: Parents must exist. If not, promote?
                if (!parentId && sectionStack[0]) parentId = sectionStack[0].id; // Fallback to category
            } else if (trimmed.startsWith('### ')) {
                depth = 1; // Subcategory
                headerTitle = trimmed.substring(4).trim();
                parentId = sectionStack[0]?.id || null;
            } else if (trimmed.startsWith('## ')) {
                depth = 0; // Category
                headerTitle = trimmed.substring(3).trim();
                parentId = null;
            } else {
                // Just content if it's "#####" or something not supported?
                // Or maybe treated as content for current section.
                currentSectionContent.push(line);
                continue;
            }

            const newSection: Section = {
                id: generateId(),
                title: headerTitle,
                content: "",
                lastUpdated: Date.now(),
                parentId: parentId,
                order: sections.length // Simple ordering based on appearance
            };

            currentSection = newSection;
            currentSectionContent = [];

            // Update Stack
            sectionStack[depth] = newSection;
            // Clear deeper levels
            for (let d = depth + 1; d < 3; d++) sectionStack[d] = null;

            continue;
        }

        // Content
        if (currentSection) {
            currentSectionContent.push(line);
        } else {
            // Project Description (before any sections)
            projectContentLines.push(line);
        }
    }

    // Finalize last section
    finalizeCurrentSection();

    // Process Project Level Content
    const rawProjectContent = projectContentLines.join('\n').trim();
    const { cleanText: projectContent, attachments: projectAttachments } = extractAttachments(rawProjectContent);
    attachments.push(...projectAttachments);

    return {
        title,
        content: projectContent,
        sections,
        attachments
    };
};
